// Helpers to rank product search suggestion groups so the group containing the
// closest match to the user's search term is shown first.

function getProductText(product) {
    if (!product) return ''
    const manufacturer = (product.manufacturer || product.Manufacturer || '').trim()
    const name = (product.productName || product.ProductName || product.ItemDescription || '').trim()

    // The product name usually already contains the manufacturer
    // (e.g. "GAF WeatherBlocker Starter Strip"). Only prefix it when it does not,
    // otherwise the duplicated word breaks prefix/contains matching.
    const text = (manufacturer && !name.toLowerCase().startsWith(manufacturer.toLowerCase()))
        ? `${manufacturer} ${name}`
        : name

    return text.trim().toLowerCase()
}

// Full searchable text of a product, including the fields the legacy search used.
function getProductSearchText(product) {
    if (!product) return ''

    const values = [
        product.manufacturer || product.Manufacturer,
        product.productName || product.ProductName,
        product.webName || product.WebName,
        product.webDescription || product.WebDescription,
        product.ItemDescription,
        product.ItemCode,
        product.Category || product.ProductCategory
    ]

    const variants = product.variants || product.Variants || []
    variants.forEach(v => {
        if (!v) return
        values.push(v.itemCode || v.ItemCode)
        values.push(v.colorName || v.ColorName)
        values.push(v.sizeName || v.SizeName)
    })

    return values.filter(Boolean).join(' ').toLowerCase()
}

// A product matches only when EVERY term of the search is found.
// Using AND semantics (instead of the legacy OR) avoids showing unrelated
// products that only share one common word such as the manufacturer name.
export function productMatchesSearch(product, searchTerm) {
    const terms = (searchTerm || '').trim().toLowerCase().split(/\s+/).filter(Boolean)
    if (terms.length === 0) return true

    const text = getProductSearchText(product)
    if (!text) return false

    return terms.every(term => text.includes(term))
}

export function filterProductsBySearch(products, searchTerm) {
    const term = (searchTerm || '').trim()
    if (!term) return products
    return (products || []).filter(p => productMatchesSearch(p, term))
}

// Higher score = better match.
// 3 = exact match, 2 = starts with the term, 1 = contains the term, 0 = no match.
export function getProductMatchScore(product, searchTerm) {
    const term = (searchTerm || '').trim().toLowerCase()
    if (!term) return 0

    const text = getProductText(product)
    if (!text) return 0

    if (text === term) return 3
    if (text.startsWith(term)) return 2
    if (text.includes(term)) return 1
    return 0
}

export function getGroupMatchScore(group, searchTerm) {
    const items = (group && group.items) || []
    return items.reduce((best, item) => Math.max(best, getProductMatchScore(item, searchTerm)), 0)
}

// Stable sort of the products inside a group so the closest match is the first
// row. Items with an equal score keep the original (API) order.
export function sortItemsByMatch(items, searchTerm) {
    const term = (searchTerm || '').trim()
    if (!term || !Array.isArray(items)) return items

    return items
        .map((item, index) => ({ item, index, score: getProductMatchScore(item, term) }))
        .sort((a, b) => (b.score - a.score) || (a.index - b.index))
        .map(entry => entry.item)
}

// Stable sort of suggestion groups by best match, preserving the original
// (API) order for groups with an equal score. The products inside each group
// are sorted as well. Returns new group objects, the source data is never mutated.
export function sortGroupsByMatch(groups, searchTerm) {
    const term = (searchTerm || '').trim()
    if (!term) return groups

    return groups
        .map((group, index) => ({ group, index, score: getGroupMatchScore(group, term) }))
        .sort((a, b) => (b.score - a.score) || (a.index - b.index))
        .map(entry => ({ ...entry.group, items: sortItemsByMatch(entry.group.items, term) }))
}
